// Idioma de la interfaz (español o inglés).
//
// Convención: la clave de cada texto ES el texto en español, tal cual aparece hoy en
// el código: t('Nueva persona'). Las traducciones al inglés viven en src/i18n/en/*.ts.
// Variables con llaves: t('Página {page} de {pages}', { page, pages }).
// Si el mismo texto en español necesita dos traducciones distintas, se agrega un
// contexto después de @@ que no se muestra: t('Donaciones@@módulo') → «Donaciones» / «Giving».
//
// El idioma se fija al cargar la página (antes de evaluar cualquier otro módulo), así
// que también funciona en constantes a nivel de módulo. Cambiar de idioma recarga la página.
import { EN } from '../i18n/en';

export type Lang = 'es' | 'en';
const KEY = 'ui.lang';
/** Idioma elegido solo para esta pestaña (kiosko): no cambia la preferencia de la persona. */
const SESSION_KEY = 'ui.lang.session';

function detect(): Lang {
  try {
    const session = sessionStorage.getItem(SESSION_KEY);
    if (session === 'es' || session === 'en') return session;
  } catch {
    /* almacenamiento no disponible */
  }
  try {
    const saved = localStorage.getItem(KEY);
    if (saved === 'es' || saved === 'en') return saved;
  } catch {
    /* almacenamiento no disponible */
  }
  const nav = typeof navigator !== 'undefined' ? navigator.language || '' : '';
  return nav.toLowerCase().startsWith('en') ? 'en' : 'es';
}

let current: Lang = detect();
if (typeof document !== 'undefined') document.documentElement.lang = current;

export function lang(): Lang {
  return current;
}

/** Locale para Intl (fechas, números, monedas). */
export function locale(): string {
  return current === 'en' ? 'en-US' : 'es-US';
}

/** Idioma que la persona eligió en este navegador (null si nunca eligió). */
export function savedLang(): Lang | null {
  try {
    const saved = localStorage.getItem(KEY);
    return saved === 'es' || saved === 'en' ? saved : null;
  } catch {
    return null;
  }
}

/** Cambia el idioma, lo recuerda en este navegador y recarga la página. */
export function setLang(next: Lang, opts: { sessionOnly?: boolean } = {}) {
  if (next === current) return;
  try {
    if (opts.sessionOnly) sessionStorage.setItem(SESSION_KEY, next);
    else {
      sessionStorage.removeItem(SESSION_KEY);
      localStorage.setItem(KEY, next);
    }
  } catch {
    /* sin almacenamiento: solo dura esta visita */
  }
  current = next;
  window.location.reload();
}

type Vars = Record<string, string | number | null | undefined>;

function fill(text: string, vars?: Vars) {
  if (!vars) return text;
  return text.replace(/\{(\w+)\}/g, (m, k: string) => (k in vars ? String(vars[k] ?? '') : m));
}

const warned = new Set<string>();

function stripContext(es: string) {
  const i = es.indexOf('@@');
  return i === -1 ? es : es.slice(0, i);
}

/** Traduce un texto escrito en español. */
export function t(es: string, vars?: Vars): string {
  if (current === 'es') return fill(stripContext(es), vars);
  const en = EN[es];
  if (en === undefined) {
    if (import.meta.env.DEV && !warned.has(es)) {
      warned.add(es);
      console.warn(`[i18n] Falta traducción: ${JSON.stringify(es)}`);
    }
    return fill(stripContext(es), vars);
  }
  return fill(en, vars);
}

/** Singular o plural según n: tp(n, '{n} persona', '{n} personas'). */
export function tp(n: number, one: string, other: string, vars?: Vars): string {
  return t(n === 1 ? one : other, { n, ...vars });
}
